import { IWorkDay, IWorkingSchedule, WorkingHours } from '../../types/work-week'
import { ShortestLeaveIntervalEnum } from '../../types/leave-policy'
import { IQuotaAmount } from '../../types/leave-policy'
import { computeDaysAndHoursForLeaveDays } from '../../functions/get-days-or-hours'
import { ILeaveDayDetails } from '../../types/leave-request'
import { getLeaveHoursForCurrentAndNextPeriod } from '../../functions/leave-duration'
import { formatQuotaInDaysOrHours, getRawQuotaDaysOrHours } from '../../functions/quotas'
import { getAverageWorkingHours } from '../../functions/work-week'

interface IGetRequestedDaysAmount {
  shortestLeaveInterval: ShortestLeaveIntervalEnum
  leaveDaysWithWorkingHours: IWorkDay[] | ILeaveDayDetails[] // difference is that in IWorkDay is Date object, and in ILeaveDayDetails is datestring
  nextYearStart: Date
  period: 'current' | 'next'
  userWorkingHours: WorkingHours | IWorkingSchedule
}

export const getRequestedDaysAmount = ({ shortestLeaveInterval, leaveDaysWithWorkingHours, nextYearStart, period, userWorkingHours }: IGetRequestedDaysAmount): IQuotaAmount => {
  const requestedDaysForPeriods = getLeaveHoursForCurrentAndNextPeriod(leaveDaysWithWorkingHours, nextYearStart)

  const requestedHours = requestedDaysForPeriods[period].usedHours
  const requestedDaysForPeriod = requestedDaysForPeriods[period].leaves

  const resultRequestedDays = computeDaysAndHoursForLeaveDays(requestedDaysForPeriod, userWorkingHours, shortestLeaveInterval)

  const fullDays = requestedDaysForPeriod.filter((workday) => workday.leaveHours === workday.workingHoursInDay).length
  const hoursWithMinutes = requestedDaysForPeriod.filter((workday) => workday.leaveHours !== workday.workingHoursInDay)
  const partialDays = hoursWithMinutes.reduce((acc, workday) => acc + workday.leaveHours / workday.workingHoursInDay, 0)

  return {
    raw: {
      days: fullDays + partialDays,
      hours: requestedHours,
    },
    days: resultRequestedDays.days,
    hours: resultRequestedDays.hours,
    minutes: resultRequestedDays.minutes,
    isNegative: resultRequestedDays.isNegative,
  }
}

export interface ISumQuotaAmounts {
  amounts: IQuotaAmount[]
  hourlyLeaveAccounting: boolean
  workingHours: WorkingHours | IWorkingSchedule
  shortestLeaveInterval: ShortestLeaveIntervalEnum
}

const zeroQuotaAmount = (): IQuotaAmount => ({ raw: { days: 0, hours: 0 }, days: 0, hours: 0, minutes: 0, isNegative: false })

// Returns undefined when the amounts can't be formatted, so callers fall back to an unsummed value
// instead of rendering a zero that would stop the row from adding up.
export const sumQuotaAmounts = ({ amounts, hourlyLeaveAccounting, workingHours, shortestLeaveInterval }: ISumQuotaAmounts): IQuotaAmount | undefined => {
  const averageWorkingHours = getAverageWorkingHours(workingHours)
  if (!averageWorkingHours) {
    return undefined
  }

  const total = amounts.reduce((acc, amount) => acc + getRawQuotaDaysOrHours(hourlyLeaveAccounting, amount), 0)
  if (total <= 0) {
    return zeroQuotaAmount()
  }

  // Mirrors backend formatQuotas so the summed box is shaped like its siblings
  const showMinutes = !Number.isInteger(averageWorkingHours) || [ShortestLeaveIntervalEnum.halfHour, ShortestLeaveIntervalEnum.fifteenMinutes].includes(shortestLeaveInterval)

  return formatQuotaInDaysOrHours(hourlyLeaveAccounting, total, workingHours, showMinutes)
}
